/**
 * PetPortraitCreator - Unified pet portrait creation interface
 * Uses the sentence-style interface - now in extra-wide modal
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import petStyles from '../assets/pet-style-previews.json';
import artStyles from '../assets/art-style-previews.json';
import backgrounds from '../assets/background-previews.json';
import ComboBox from './ComboBox';

// Change "Other" to more descriptive text
const withOther = arr => {
  return arr.some(opt => opt.key === 'other') ? arr : [...arr, { key: 'other', label: '...or type anything you want' }];
};

const petStyleExamples = ['Superhero', 'Pirate', 'Princess'];
const artStyleExamples = ['Watercolor', 'Cartoon', 'Anime'];
const backgroundExamples = ['Sunny Park', 'Beach', 'Outer Space'];

export default function PetPortraitCreator({ 
  onComplete, 
  onClose, 
  isModal = false,
  isWide = false,
  initialImage = null,
  initialPetName = '',
  productName = null,
  preSelectedStyle = '',
  preSelectedBackground = '',
  preSelectedCostume = '',
}) {
  // Upload state
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(initialImage);
  const [dragActive, setDragActive] = useState(false);
  
  // Form state - initialize with pre-selected values from inspiration gallery
  const [petName, setPetName] = useState(initialPetName);
  const [petStyle, setPetStyle] = useState(preSelectedCostume || '');
  const [artStyle, setArtStyle] = useState(preSelectedStyle || '');
  const [background, setBackground] = useState(preSelectedBackground || '');
  
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generatedImage, setGeneratedImage] = useState(null);
  
  // UI state
  const nameSpanRef = useRef();
  const [nameWidth, setNameWidth] = useState(120);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const fileInputRef = useRef();

  // Inject animations
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'pet-portrait-creator-animations';
    styleSheet.textContent = `
      @keyframes slideInFromRight {
        from { transform: translateX(100%); }
        to { transform: translateX(0); }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes pulseGlow {
        0%, 100% { box-shadow: 0 0 20px rgba(147, 51, 234, 0.3); }
        50% { box-shadow: 0 0 40px rgba(147, 51, 234, 0.6); }
      }
      @keyframes sparkle {
        0%, 100% { opacity: 1; transform: scale(1) rotate(0deg); }
        50% { opacity: 0.7; transform: scale(1.2) rotate(180deg); }
      }
      @keyframes bounceIn {
        0% { transform: scale(0.8); opacity: 0; }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-6px); }
      }
      .portrait-creator-modal {
        animation: slideInFromRight 0.3s ease-out;
      }
      .portrait-creator-overlay {
        animation: fadeIn 0.2s ease-out;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      const existing = document.getElementById('pet-portrait-creator-animations');
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (nameSpanRef.current) {
        setNameWidth(Math.min(Math.max(nameSpanRef.current.offsetWidth + 32, 120), isMobile ? 220 : 420));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  useEffect(() => {
    if (nameSpanRef.current) {
      setNameWidth(Math.min(Math.max(nameSpanRef.current.offsetWidth + 32, 120), isMobile ? 220 : 420));
    }
  }, [petName, isMobile]);

  // File handling
  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  }, []);

  const clearPhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
  };

  // Generation
  const canGenerate = photoPreview && petName && artStyle;
  
  const handleGenerate = async () => {
    if (!canGenerate) return;
    
    setIsGenerating(true);
    setProgress(0);
    
    for (let i = 0; i <= 100; i += 2) {
      await new Promise(r => setTimeout(r, 60));
      setProgress(i);
    }
    
    setGeneratedImage(photoPreview);
    setIsGenerating(false);
  };

  const handleComplete = () => {
    onComplete?.({
      image: generatedImage,
      petName,
      petStyle,
      artStyle,
      background,
    });
  };

  const getProgressMessage = () => {
    if (progress < 20) return '🎨 Mixing paints...';
    if (progress < 40) return '✨ Adding sparkles...';
    if (progress < 60) return '🖌️ Painting details...';
    if (progress < 80) return '🌈 Adding colors...';
    return '✨ Almost done!';
  };

  // Extra wide modal - 900px
  const modalWidth = isWide ? '900px' : '500px';
  const previewSize = isWide ? '320px' : '200px';

  // Render generating state
  if (isGenerating) {
    return (
      <div style={isModal ? styles.modalWrapper : {}}>
        {isModal && <div className="portrait-creator-overlay" style={styles.overlay} onClick={onClose} />}
        <div 
          className={isModal ? "portrait-creator-modal" : ""} 
          style={isModal ? {...styles.modalContent, maxWidth: modalWidth} : styles.container}
        >
          {isModal && (
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>✨ Creating Portrait</h2>
              <button onClick={onClose} style={styles.closeBtn}>✕</button>
            </div>
          )}
          
          <div style={styles.generatingState}>
            <h2 style={styles.generatingTitle}>🎨 Creating {petName}'s Portrait!</h2>
            <p style={styles.generatingSubtitle}>Sit tight, magic is happening! ✨</p>
            
            <div style={{...styles.paintingFrame, width: previewSize, height: previewSize}}>
              <img src={photoPreview} alt={petName} style={styles.paintingImage} />
              <div style={{...styles.paintingOverlay, clipPath: `inset(0 ${100 - progress}% 0 0)`}} />
            </div>
            
            <div style={styles.progressBar}>
              <div style={{...styles.progressFill, width: `${progress}%`}} />
            </div>
            <p style={styles.progressMessage}>{getProgressMessage()}</p>
            <p style={styles.progressPercent}>{progress}%</p>
          </div>
        </div>
      </div>
    );
  }

  // Render result state
  if (generatedImage) {
    return (
      <div style={isModal ? styles.modalWrapper : {}}>
        {isModal && <div className="portrait-creator-overlay" style={styles.overlay} onClick={onClose} />}
        <div 
          className={isModal ? "portrait-creator-modal" : ""} 
          style={isModal ? {...styles.modalContent, maxWidth: modalWidth} : styles.container}
        >
          {isModal && (
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>🎉 Portrait Ready!</h2>
              <button onClick={onClose} style={styles.closeBtn}>✕</button>
            </div>
          )}
          
          <div style={styles.resultState}>
            <div style={styles.resultHeader}>
              <span style={styles.celebrateEmoji}>🎉</span>
              <h2 style={styles.resultTitle}>Meet {petName}!</h2>
              <span style={styles.celebrateEmoji}>🎉</span>
            </div>
            <p style={styles.resultSubtitle}>Your portrait is ready! Looking good! 😎</p>
            
            {/* Transformation View - Original → New */}
            <div style={styles.transformationView}>
              {/* Original Photo */}
              <div style={styles.originalColumn}>
                <span style={styles.transformLabel}>📷 Original</span>
                <div style={styles.originalThumb}>
                  <img src={photoPreview} alt="Original" style={styles.originalThumbImg} />
                </div>
              </div>
              
              {/* Arrow */}
              <div style={styles.transformArrow}>
                <span style={styles.arrowEmoji}>✨</span>
                <span style={styles.arrowIcon}>→</span>
                <span style={styles.arrowEmoji}>🎨</span>
              </div>
              
              {/* Generated Portrait - Large */}
              <div style={styles.generatedColumn}>
                <span style={styles.transformLabel}>🖼️ Portrait</span>
                <div style={styles.resultImageWrapper}>
                  <img 
                    src={generatedImage} 
                    alt={petName} 
                    style={{...styles.resultImage, maxHeight: isWide ? '450px' : '320px'}} 
                  />
                </div>
              </div>
            </div>
            
            <div style={styles.resultActions}>
              <button onClick={handleComplete} style={styles.primaryBtn} className="fun-button">
                {isModal ? '✓ Use This Portrait' : '🛍️ Put on Products'}
              </button>
              
              <button 
                onClick={() => {
                  setGeneratedImage(null);
                  setProgress(0);
                }} 
                style={styles.createAnotherBtn}
              >
                🔄 Create Another Version
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main content - Wide layout with sentence interface + large preview
  const content = isWide ? (
    <div style={styles.wideLayout}>
      {/* Left: Large Upload Zone */}
      <div style={styles.wideLeft}>
        <h3 style={styles.wideSectionTitle}>📸 Your Pet Photo</h3>
        <div
          style={{
            ...styles.uploadZoneWide,
            borderColor: dragActive ? '#9333ea' : '#c4b5fd',
            background: dragActive ? 'rgba(147, 51, 234, 0.05)' : '#faf5ff',
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="portrait-creator-upload"
          />
          
          {photoPreview ? (
            <div style={styles.photoPreviewWide}>
              <img src={photoPreview} alt="Your pet" style={styles.photoPreviewImageWide} />
              <button onClick={clearPhoto} style={styles.changePhotoBtn}>
                📷 Change Photo
              </button>
            </div>
          ) : (
            <label htmlFor="portrait-creator-upload" style={styles.uploadLabelWide}>
              <div style={styles.uploadIconCircleWide}>
                <span style={styles.uploadIconWide}>📷</span>
              </div>
              <span style={styles.uploadTextWide}>
                <span style={styles.uploadHighlight}>Click to upload</span>
                <br />or drag and drop
              </span>
              <span style={styles.uploadFormats}>PNG, JPG or GIF</span>
            </label>
          )}
        </div>
        
        <div style={styles.photoTips}>
          <p style={styles.photoTipsTitle}>📸 Tips for best results:</p>
          <ul style={styles.photoTipsList}>
            <li>Clear photo of your pet's face</li>
            <li>Good lighting (no shadows)</li>
            <li>Pet looking at camera</li>
          </ul>
        </div>
      </div>
      
      {/* Right: Sentence Interface */}
      <div style={styles.wideRight}>
        <h3 style={styles.wideSectionTitle}>🎨 Style Your Portrait</h3>
        
        {/* Sentence Interface in Wide Modal */}
        <div style={styles.sentenceCardWide}>
          <div style={styles.sentenceWide}>
            <span style={styles.sentenceText}>Make my pet named </span>
            <input
              type="text"
              placeholder="Pet Name"
              value={petName}
              onChange={e => setPetName(e.target.value)}
              style={{
                ...styles.nameInputWide,
                width: Math.max(140, petName.length * 14 + 40),
              }}
              autoComplete="off"
            />
            <span style={styles.sentenceText}> into a </span>
            <ComboBox
              options={withOther(petStyles)}
              value={petStyle}
              onChange={setPetStyle}
              placeholder="costume"
              autoGrow
              examples={petStyle === 'other' ? petStyleExamples : undefined}
            />
            <span style={styles.sentenceText}> with </span>
            <ComboBox
              options={withOther(artStyles)}
              value={artStyle}
              onChange={setArtStyle}
              placeholder="art style"
              autoGrow
              examples={artStyle === 'other' ? artStyleExamples : undefined}
            />
            <span style={styles.sentenceText}> style, on a </span>
            <ComboBox
              options={withOther(backgrounds)}
              value={background}
              onChange={setBackground}
              placeholder="background"
              autoGrow
              examples={background === 'other' ? backgroundExamples : undefined}
            />
            <span style={styles.sentenceText}> background.</span>
          </div>
        </div>

        {/* Transform Button */}
        <button
          onClick={handleGenerate}
          disabled={!canGenerate}
          style={{
            ...styles.transformBtn,
            opacity: canGenerate ? 1 : 0.6,
            cursor: canGenerate ? 'pointer' : 'not-allowed',
          }}
        >
          <span style={styles.transformSparkle}>✨</span>
          Transform My Pet!
        </button>

        {!canGenerate && (
          <p style={styles.hint}>
            {!photoPreview 
              ? "👈 Upload a photo first" 
              : !petName 
                ? "👆 Enter your pet's name"
                : "👆 Pick an art style"}
          </p>
        )}
      </div>
    </div>
  ) : (
    // Original compact layout
    <div style={styles.contentWrapper}>
      {/* Upload Zone */}
      <div
        style={{
          ...styles.uploadZone,
          borderColor: dragActive ? '#9333ea' : '#c4b5fd',
          background: dragActive ? 'rgba(147, 51, 234, 0.05)' : '#faf5ff',
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id="portrait-creator-upload-compact"
        />
        
        {photoPreview ? (
          <div style={styles.photoPreviewWrapper}>
            <img src={photoPreview} alt="Your pet" style={styles.photoPreviewImage} />
            <button onClick={clearPhoto} style={styles.changePhotoBtn}>
              📷 Change Photo
            </button>
          </div>
        ) : (
          <label htmlFor="portrait-creator-upload-compact" style={styles.uploadLabel}>
            <div style={styles.uploadIconCircle}>
              <span style={styles.uploadIcon}>⬇</span>
            </div>
            <span style={styles.uploadText}>
              <span style={styles.uploadHighlight}>Click to upload</span> or drag and drop
            </span>
            <span style={styles.uploadFormats}>PNG, JPG or GIF</span>
          </label>
        )}
      </div>

      {/* Sentence Interface */}
      <div style={styles.sentenceCard}>
        {isMobile ? (
          <div className="nl-prompt-mobile">
            <div style={styles.sentenceMobile}>
              Make my pet named
              <input
                type="text"
                placeholder="Pet Name"
                value={petName}
                onChange={e => setPetName(e.target.value)}
                style={styles.nameInputMobile}
                autoComplete="off"
              />
              into a
              <ComboBox
                options={withOther(petStyles)}
                value={petStyle}
                onChange={setPetStyle}
                placeholder="Pet Style"
                examples={petStyle === 'other' ? petStyleExamples : undefined}
                fullWidth
              />
              with
              <ComboBox
                options={withOther(artStyles)}
                value={artStyle}
                onChange={setArtStyle}
                placeholder="Art Style"
                examples={artStyle === 'other' ? artStyleExamples : undefined}
                fullWidth
              />
              style, on a
              <ComboBox
                options={withOther(backgrounds)}
                value={background}
                onChange={setBackground}
                placeholder="Background"
                examples={background === 'other' ? backgroundExamples : undefined}
                fullWidth
              />
              background.
            </div>
          </div>
        ) : (
          <div style={styles.sentenceDesktop}>
            <span>Make my pet named </span>
            <input
              type="text"
              placeholder="Pet Name"
              value={petName}
              onChange={e => setPetName(e.target.value)}
              style={{
                ...styles.nameInputDesktop,
                width: nameWidth,
                color: petName ? '#222' : '#B0B0B0',
              }}
              autoComplete="off"
            />
            <span
              ref={nameSpanRef}
              style={styles.hiddenMeasure}
            >
              {petName || 'Pet Name'}
            </span>
            <span>into a </span>
            <ComboBox
              options={withOther(petStyles)}
              value={petStyle}
              onChange={setPetStyle}
              placeholder="Pet Style"
              autoGrow
              examples={petStyle === 'other' ? petStyleExamples : undefined}
            />
            <span>with </span>
            <ComboBox
              options={withOther(artStyles)}
              value={artStyle}
              onChange={setArtStyle}
              placeholder="Art Style"
              autoGrow
              examples={artStyle === 'other' ? artStyleExamples : undefined}
            />
            <span>style, on a </span>
            <ComboBox
              options={withOther(backgrounds)}
              value={background}
              onChange={setBackground}
              placeholder="Background"
              autoGrow
              examples={background === 'other' ? backgroundExamples : undefined}
            />
            <span>background.</span>
          </div>
        )}
      </div>

      {/* Transform Button */}
      <button
        onClick={handleGenerate}
        disabled={!canGenerate}
        style={{
          ...styles.transformBtn,
          opacity: canGenerate ? 1 : 0.6,
          cursor: canGenerate ? 'pointer' : 'not-allowed',
        }}
      >
        <span style={styles.transformSparkle}>✨</span>
        Transform My Pet!
      </button>

      {!canGenerate && (
        <p style={styles.hint}>
          {!photoPreview 
            ? "👆 Upload a photo of your pet first" 
            : !petName 
              ? "👆 Enter your pet's name"
              : "👆 Pick an art style"}
        </p>
      )}
    </div>
  );

  // Step indicator for modal
  const modalSteps = productName ? [
    { id: 1, emoji: '🛍️', label: 'Product', done: true },
    { id: 2, emoji: '📸', label: 'Upload & Style', active: !generatedImage && !isGenerating },
    { id: 3, emoji: '🎉', label: 'Done!', active: generatedImage },
  ] : [
    { id: 1, emoji: '📸', label: 'Upload & Style', active: !generatedImage && !isGenerating },
    { id: 2, emoji: '🎉', label: 'Done!', active: generatedImage },
  ];

  // Wrap in modal if needed
  if (isModal) {
    return (
      <div style={styles.modalWrapper}>
        <div className="portrait-creator-overlay" style={styles.overlay} onClick={onClose} />
        <div 
          className="portrait-creator-modal" 
          style={{...styles.modalContent, maxWidth: modalWidth}}
        >
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>✨ Create Pet Portrait</h2>
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
          </div>
          
          {/* Step Progress in Modal */}
          <div style={styles.modalStepsContainer}>
            <div style={styles.modalStepsRow}>
              {modalSteps.map((step, idx) => (
                <React.Fragment key={step.id}>
                  {idx > 0 && <div style={styles.modalStepConnector} />}
                  <div style={styles.modalStepItem}>
                    <div style={{
                      ...styles.modalStepCircle,
                      background: step.done 
                        ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                        : step.active 
                          ? 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)'
                          : '#e5e7eb',
                    }}>
                      {step.done ? <span style={styles.modalStepCheck}>✓</span> : <span>{step.emoji}</span>}
                    </div>
                    <span style={{
                      ...styles.modalStepLabel,
                      color: step.done || step.active ? '#374151' : '#9ca3af',
                      fontWeight: step.active ? '700' : '500',
                    }}>{step.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
            {productName && (
              <div style={styles.productSelectedBadge}>
                <span>🛍️</span> {productName}
              </div>
            )}
          </div>
          
          <div style={styles.modalBody}>
            {content}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {content}
    </div>
  );
}

const styles = {
  container: {
    padding: '24px',
    maxWidth: '600px',
    margin: '0 auto',
    position: 'relative',
  },
  contentWrapper: {
    position: 'relative',
    zIndex: 1,
    paddingBottom: '100px',
  },
  
  // Modal styles
  modalWrapper: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
  },
  modalContent: {
    position: 'relative',
    width: '100%',
    height: '100%',
    background: '#fff',
    boxShadow: '-8px 0 32px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1001,
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 32px',
    borderBottom: '1px solid #e5e7eb',
    background: 'linear-gradient(135deg, #faf5ff 0%, #fdf4ff 100%)',
    flexShrink: 0,
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1f2937',
    margin: 0,
  },
  closeBtn: {
    width: '40px',
    height: '40px',
    minWidth: '40px',
    minHeight: '40px',
    maxWidth: '40px',
    maxHeight: '40px',
    borderRadius: '50%',
    border: 'none',
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    fontSize: '16px',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '400',
    boxShadow: '0 2px 8px rgba(147, 51, 234, 0.3)',
    flexShrink: 0,
  },
  modalBody: {
    flex: 1,
    padding: '32px',
    overflowY: 'auto',
    overflowX: 'visible',
  },
  
  // Modal step progress
  modalStepsContainer: {
    padding: '16px 32px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fafafa',
    flexShrink: 0,
  },
  modalStepsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  modalStepItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  modalStepCircle: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
  },
  modalStepCheck: {
    color: '#fff',
    fontWeight: '700',
  },
  modalStepLabel: {
    fontSize: '12px',
    whiteSpace: 'nowrap',
  },
  modalStepConnector: {
    width: '40px',
    height: '3px',
    background: '#e5e7eb',
    borderRadius: '2px',
    marginBottom: '24px',
  },
  productSelectedBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    marginTop: '12px',
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)',
    borderRadius: '50px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#166534',
  },
  
  // Wide Layout (two columns) - Now even wider
  wideLayout: {
    display: 'grid',
    gridTemplateColumns: '380px 1fr',
    gap: '40px',
    paddingBottom: '40px',
  },
  wideLeft: {},
  wideRight: {},
  wideSectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1f2937',
    margin: 0,
    marginBottom: '16px',
  },
  
  // Wide Upload Zone - Larger
  uploadZoneWide: {
    border: '2px dashed',
    borderRadius: '24px',
    padding: '32px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minHeight: '320px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadLabelWide: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    gap: '12px',
  },
  uploadIconCircleWide: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px',
    boxShadow: '0 4px 20px rgba(147, 51, 234, 0.3)',
  },
  uploadIconWide: {
    fontSize: '36px',
  },
  uploadTextWide: {
    fontSize: '16px',
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 1.5,
  },
  photoPreviewWide: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  photoPreviewImageWide: {
    maxWidth: '300px',
    maxHeight: '300px',
    borderRadius: '20px',
    objectFit: 'cover',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
  },
  photoTips: {
    marginTop: '20px',
    background: '#f9fafb',
    borderRadius: '16px',
    padding: '20px',
  },
  photoTipsTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    margin: 0,
    marginBottom: '10px',
  },
  photoTipsList: {
    margin: 0,
    paddingLeft: '20px',
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: 2,
  },
  
  // Wide Sentence Card - Sentence Interface
  sentenceCardWide: {
    background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
    borderRadius: '24px',
    padding: '32px',
    marginBottom: '24px',
  },
  sentenceWide: {
    fontSize: '22px',
    fontWeight: '500',
    color: '#374151',
    lineHeight: 2.2,
  },
  sentenceText: {
    color: '#374151',
  },
  nameInputWide: {
    fontSize: '20px',
    fontWeight: '600',
    border: 'none',
    borderBottom: '2px solid #A389F4',
    background: 'white',
    padding: '4px 12px',
    margin: '0 4px',
    borderRadius: '8px',
    color: '#1f2937',
    outline: 'none',
    transition: 'width 0.2s ease',
  },
  
  // Original Upload Zone
  uploadZone: {
    border: '2px dashed',
    borderRadius: '20px',
    padding: '24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: '20px',
  },
  uploadLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    gap: '8px',
  },
  uploadIconCircle: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'rgba(147, 51, 234, 0.1)',
    border: '2px solid #c4b5fd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadIcon: {
    fontSize: '20px',
    color: '#9333ea',
  },
  uploadText: {
    fontSize: '15px',
    color: '#6b7280',
  },
  uploadHighlight: {
    color: '#9333ea',
    fontWeight: '600',
  },
  uploadFormats: {
    fontSize: '13px',
    color: '#9ca3af',
  },
  photoPreviewWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  photoPreviewImage: {
    maxWidth: '160px',
    maxHeight: '160px',
    borderRadius: '16px',
    objectFit: 'cover',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
  changePhotoBtn: {
    background: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: '50px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    cursor: 'pointer',
  },
  
  // Sentence Card
  sentenceCard: {
    background: 'rgb(243, 240, 255)',
    borderRadius: '24px',
    padding: '24px',
    marginBottom: '20px',
    fontSize: '24px',
    fontWeight: '500',
    color: '#444',
    position: 'relative',
    overflow: 'visible',
    zIndex: 10,
  },
  sentenceMobile: {
    marginBottom: '16px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#222',
    fontSize: '16px',
  },
  nameInputMobile: {
    fontSize: '15px',
    fontWeight: '600',
    width: '100%',
    padding: '8px 14px',
    borderRadius: '10px',
    border: '2px solid #A389F4',
    marginTop: '8px',
    marginBottom: '8px',
    background: 'white',
  },
  sentenceDesktop: {
    lineHeight: 1.9,
  },
  nameInputDesktop: {
    fontSize: '22px',
    fontWeight: '600',
    border: 'none',
    borderBottom: '2px solid #A389F4',
    background: 'white',
    minWidth: '120px',
    maxWidth: '420px',
    margin: '0 8px',
    paddingLeft: '12px',
    transition: 'width 0.2s, color 0.2s',
    borderRadius: '8px',
  },
  hiddenMeasure: {
    position: 'absolute',
    visibility: 'hidden',
    height: 0,
    overflow: 'hidden',
    whiteSpace: 'pre',
    fontSize: '22px',
    fontWeight: '600',
    fontFamily: 'inherit',
    pointerEvents: 'none',
    paddingLeft: '12px',
  },
  
  // Transform Button
  transformBtn: {
    width: '100%',
    padding: '18px 24px',
    fontSize: '20px',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '16px',
    color: '#fff',
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(147, 51, 234, 0.35)',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    position: 'relative',
    zIndex: 1,
  },
  transformSparkle: {
    fontSize: '20px',
    animation: 'sparkle 2s ease-in-out infinite',
  },
  hint: {
    textAlign: 'center',
    fontSize: '15px',
    color: '#9ca3af',
    marginTop: '16px',
  },
  
  // Generating state
  generatingState: {
    textAlign: 'center',
    padding: '48px 32px',
  },
  generatingTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '8px',
  },
  generatingSubtitle: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '40px',
  },
  paintingFrame: {
    margin: '0 auto 32px',
    borderRadius: '24px',
    overflow: 'hidden',
    border: '4px solid #9333ea',
    position: 'relative',
    boxShadow: '0 8px 32px rgba(147, 51, 234, 0.3)',
    animation: 'pulseGlow 2s ease-in-out infinite',
  },
  paintingImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  paintingOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.5) 0%, rgba(236, 72, 153, 0.5) 100%)',
    transition: 'clip-path 0.3s ease',
  },
  progressBar: {
    width: '100%',
    maxWidth: '350px',
    height: '12px',
    background: 'rgba(147, 51, 234, 0.1)',
    borderRadius: '6px',
    overflow: 'hidden',
    margin: '0 auto 20px',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #9333ea 0%, #ec4899 100%)',
    borderRadius: '6px',
    transition: 'width 0.2s ease',
  },
  progressMessage: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#7c3aed',
    marginBottom: '8px',
  },
  progressPercent: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#9333ea',
  },
  
  // Result state
  resultState: {
    textAlign: 'center',
    padding: '32px',
  },
  resultHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginBottom: '8px',
  },
  celebrateEmoji: {
    fontSize: '40px',
    animation: 'bounceIn 0.6s ease-out',
  },
  resultTitle: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1f2937',
    margin: 0,
  },
  resultSubtitle: {
    fontSize: '18px',
    color: '#6b7280',
    marginBottom: '32px',
  },
  // Transformation view - Original → New
  transformationView: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '24px',
    marginBottom: '32px',
    flexWrap: 'wrap',
  },
  originalColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  generatedColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  transformLabel: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#7c3aed',
    fontFamily: 'inherit',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  originalThumb: {
    width: '100px',
    height: '100px',
    borderRadius: '16px',
    overflow: 'hidden',
    border: '3px solid #e9d5ff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  originalThumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  transformArrow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '0 8px',
  },
  arrowEmoji: {
    fontSize: '16px',
    animation: 'sparkle 1.5s ease-in-out infinite',
  },
  arrowIcon: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#9333ea',
  },
  resultImageWrapper: {
    display: 'inline-block',
    borderRadius: '24px',
    overflow: 'hidden',
    boxShadow: '0 12px 48px rgba(147, 51, 234, 0.25)',
  },
  resultImage: {
    display: 'block',
    maxWidth: '100%',
    objectFit: 'contain',
  },
  resultActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxWidth: '400px',
    margin: '0 auto',
  },
  createAnotherBtn: {
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: 'inherit',
    background: '#f3f4f6',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    color: '#6b7280',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  primaryBtn: {
    width: '100%',
    padding: '18px 24px',
    fontSize: '18px',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    border: 'none',
    borderRadius: '14px',
    color: '#fff',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(34, 197, 94, 0.4)',
  },
  secondaryActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  secondaryBtn: {
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    background: '#fff',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    color: '#374151',
    cursor: 'pointer',
  },
};
